
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';

// Layout Components (mantidos como imports diretos por serem críticos)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import AdminLayout from './components/layout/AdminLayout';

// Loading Component
import Loading from './components/ui/Loading';

// Lazy Loading Components
const Hero = React.lazy(() => import('./components/features/Hero'));
const Services = React.lazy(() => import('./components/features/Services'));
const About = React.lazy(() => import('./components/features/About'));
const Testimonials = React.lazy(() => import('./components/features/Testimonials'));
const ContactForm = React.lazy(() => import('./components/forms/ContactForm'));
const WhatsAppButton = React.lazy(() => import('./components/ui/WhatsAppButton'));
const PublicEvents = React.lazy(() => import('./components/features/PublicEvents'));
const EventDetails = React.lazy(() => import('./components/features/EventDetails'));
const EventMural = React.lazy(() => import('./components/features/EventMural'));
const EventTelao = React.lazy(() => import('./components/features/EventTelao'));
const PublicEventOrders = React.lazy(() => import('./components/features/PublicEventOrders'));
const PublicEventTicketRescue = React.lazy(() => import('./components/features/PublicEventTicketRescue'));

// Admin Components (Lazy Loading)
const AdminLogin = React.lazy(() => import('./components/forms/AdminLogin'));
const AdminDashboard = React.lazy(() => import('./components/features/AdminDashboard'));
const AdminClients = React.lazy(() => import('./components/features/AdminClients'));
const AdminEvents = React.lazy(() => import('./components/features/AdminEvents'));
const AdminTestimonials = React.lazy(() => import('./components/features/AdminTestimonials'));
const AdminSettings = React.lazy(() => import('./components/features/AdminSettings'));
const AdminSolicitations = React.lazy(() => import('./components/features/AdminSolicitations'));
const AdminEventMural = React.lazy(() => import('./components/features/AdminEventMural'));
const AdminEventContests = React.lazy(() => import('./components/features/AdminEventContests'));
const ContestKiosk = React.lazy(() => import('./components/features/ContestKiosk'));
const DigitalCheckIn = React.lazy(() => import('./components/features/DigitalCheckIn'));
const AdminPayouts = React.lazy(() => import('./components/features/AdminPayouts'));

// Supplier Components (Lazy Loading)
const AdminSuppliers = React.lazy(() => import('./components/features/AdminSuppliers'));
const AdminSupplierRegistration = React.lazy(() => import('./components/features/AdminSupplierRegistration'));
const AdminSupplierList = React.lazy(() => import('./components/features/AdminSupplierList'));
const AdminSupplierProfile = React.lazy(() => import('./components/features/AdminSupplierProfile'));
const AdminSupplierCategories = React.lazy(() => import('./components/features/AdminSupplierCategories'));
const AdminSupplierDocuments = React.lazy(() => import('./components/features/AdminSupplierDocuments'));

// Shared Components
const ProtectedRoute = React.lazy(() => import('./components/shared/ProtectedRoute'));

// Contexts
import { LanguageProvider } from './shared/contexts/contexts/LanguageContext';
import { AuthProvider } from './shared/contexts/contexts/AuthContext';
import { SettingsProvider } from './shared/contexts/contexts/SettingsContext';

// Loading Fallback Component
const LoadingFallback: React.FC<{ message?: string }> = ({ message = 'Carregando...' }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <Loading variant="pulse" size="lg" text={message} />
  </div>
);

// Componente da página principal
const HomePage: React.FC = () => {
  return (
    <div className="bg-gray-50 text-gray-800 antialiased">
      <Header />
      <main>
        <Suspense fallback={<LoadingFallback message="Carregando página inicial..." />}>
          <Hero />
          <Services />
          <About />
          <Testimonials />
          <ContactForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

const AppContent: React.FC = () => {
  // O botão do WhatsApp fala com o público do site; na área administrativa ele
  // só cobre os controles de tela (modo tablet, check-in) sem servir a ninguém.
  const { pathname } = useLocation();
  const isKioskOrAdminArea =
    pathname.startsWith('/admin') ||
    pathname.startsWith('/concursos/tablet') ||
    pathname.startsWith('/tablet') ||
    pathname.startsWith('/portaria') ||
    pathname.startsWith('/checkin') ||
    pathname.includes('/resgate') ||
    pathname.startsWith('/resgate');

  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* Rota principal */}
        <Route 
          path="/" 
          element={
            <Suspense fallback={<LoadingFallback message="Carregando página inicial..." />}>
              <HomePage />
            </Suspense>
          } 
        />
        
        {/* Rota pública de eventos */}
        <Route 
          path="/eventos" 
          element={
            <Suspense fallback={<LoadingFallback message="Carregando eventos..." />}>
              <PublicEvents />
            </Suspense>
          } 
        />
        <Route
          path="/eventos/:id"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando detalhes do evento..." />}>
              <EventDetails />
            </Suspense>
          }
        />
        <Route
          path="/eventos/:id/resgate"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando resgate de ingressos..." />}>
              <PublicEventTicketRescue />
            </Suspense>
          }
        />
        <Route
          path="/eventos/:id/ingressos"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando resgate de ingressos..." />}>
              <PublicEventTicketRescue />
            </Suspense>
          }
        />
        <Route
          path="/resgate/:id"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando resgate de ingressos..." />}>
              <PublicEventTicketRescue />
            </Suspense>
          }
        />
        <Route
          path="/resgate"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando resgate de ingressos..." />}>
              <PublicEventTicketRescue />
            </Suspense>
          }
        />
        <Route
          path="/eventos/:id/mural"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando mural..." />}>
              <EventMural />
            </Suspense>
          }
        />
        <Route
          path="/eventos/:id/telao"
          element={
            <Suspense fallback={<LoadingFallback message="Preparando telão..." />}>
              <EventTelao />
            </Suspense>
          }
        />
        <Route
          path="/pedidos/:token"
          element={
            <Suspense fallback={<LoadingFallback message="Carregando consulta..." />}>
              <PublicEventOrders />
            </Suspense>
          }
        />
        
        {/* Modo tablet público via link seguro/token (fora do admin, sem login, sem dados confidenciais) */}
        <Route
          path="/concursos/tablet/:token"
          element={
            <Suspense fallback={<LoadingFallback message="Abrindo modo tablet..." />}>
              <ContestKiosk />
            </Suspense>
          }
        />
        <Route
          path="/tablet/:token"
          element={
            <Suspense fallback={<LoadingFallback message="Abrindo modo tablet..." />}>
              <ContestKiosk />
            </Suspense>
          }
        />

        {/* Modo portaria / check-in público via link seguro/token (fora do admin, sem login, sem dados confidenciais) */}
        <Route
          path="/portaria/:token"
          element={
            <Suspense fallback={<LoadingFallback message="Abrindo portaria..." />}>
              <DigitalCheckIn />
            </Suspense>
          }
        />
        <Route
          path="/checkin/:token"
          element={
            <Suspense fallback={<LoadingFallback message="Abrindo portaria..." />}>
              <DigitalCheckIn />
            </Suspense>
          }
        />

        {/* Rotas administrativas */}
        {/* Modo tablet: tela cheia, fora do AdminLayout (sem menu lateral) */}
        <Route
          path="/admin/events/:id/concursos/:contestId/tablet"
          element={
            <Suspense fallback={<LoadingFallback message="Abrindo modo tablet..." />}>
              <ProtectedRoute>
                <ContestKiosk />
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route 
          path="/admin/login" 
          element={
            <Suspense fallback={<LoadingFallback message="Carregando login..." />}>
              <AdminLogin />
            </Suspense>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <Suspense fallback={<LoadingFallback message="Carregando área administrativa..." />}>
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            </Suspense>
          }
        >
          <Route 
            path="dashboard" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando dashboard..." />}>
                <AdminDashboard />
              </Suspense>
            } 
          />
          <Route 
            path="clients" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando clientes..." />}>
                <AdminClients />
              </Suspense>
            } 
          />
          <Route 
            path="events" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando eventos..." />}>
                <AdminEvents />
              </Suspense>
            } 
          />
          <Route
            path="events/:id/mural"
            element={
              <Suspense fallback={<LoadingFallback message="Carregando mural..." />}>
                <AdminEventMural />
              </Suspense>
            }
          />
          <Route
            path="events/:id/concursos"
            element={
              <Suspense fallback={<LoadingFallback message="Carregando concursos..." />}>
                <AdminEventContests />
              </Suspense>
            }
          />
          <Route
            path="repasses"
            element={
              <Suspense fallback={<LoadingFallback message="Carregando repasses..." />}>
                <AdminPayouts />
              </Suspense>
            }
          />
          <Route
            path="solicitations"
            element={
              <Suspense fallback={<LoadingFallback message="Carregando solicitações..." />}>
                <AdminSolicitations />
              </Suspense>
            } 
          />
          <Route 
            path="testimonials" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando depoimentos..." />}>
                <AdminTestimonials />
              </Suspense>
            } 
          />
          <Route 
            path="settings" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando configurações..." />}>
                <AdminSettings />
              </Suspense>
            } 
          />
          {/* Rotas de Fornecedores */}
          <Route 
            path="fornecedores" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando fornecedores..." />}>
                <AdminSuppliers />
              </Suspense>
            } 
          />
          <Route 
            path="fornecedores/cadastro" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando cadastro de fornecedor..." />}>
                <AdminSupplierRegistration />
              </Suspense>
            } 
          />
          <Route 
            path="fornecedores/lista" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando lista de fornecedores..." />}>
                <AdminSupplierList />
              </Suspense>
            } 
          />
          <Route 
            path="fornecedores/:id" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando perfil do fornecedor..." />}>
                <AdminSupplierProfile />
              </Suspense>
            } 
          />
          <Route 
            path="fornecedores/categorias" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando categorias..." />}>
                <AdminSupplierCategories />
              </Suspense>
            } 
          />
          <Route 
            path="fornecedores/documentos" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando documentos..." />}>
                <AdminSupplierDocuments />
              </Suspense>
            } 
          />
          <Route 
            path="" 
            element={
              <Suspense fallback={<LoadingFallback message="Carregando dashboard..." />}>
                <AdminDashboard />
              </Suspense>
            } 
          />
        </Route>
      </Routes>
      <Toaster 
        position="top-right"
        richColors
        closeButton
        duration={4000}
      />
      {!isKioskOrAdminArea && (
        <Suspense fallback={null}>
          <WhatsAppButton />
        </Suspense>
      )}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <LanguageProvider>
        <SettingsProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </SettingsProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;
